import brightcovePlayerLoader from '@brightcove/player-loader';
import * as Sentry from '@sentry/browser';

import { UserStore } from 'Main/user-store';
import VideoCountdown from 'Main/video-countdown';
import { EVENT_PLAYER_CHAPTER_CHANGE } from 'Utils/types';
import PixelTracker from '../tracking/pixel-tracker';
import createCustomComponents from './custom-video-components';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

export class BrightcovePlayer {
    constructor() {
        this._surface = 'video_page';
        this._lastReportTime = 0;
        this._reportTimeInterval = 20000; // variable, depending on the playback rate
        this._lastPlayerProgressSeconds = -1;
        this._customControls = document.querySelector('.js_videoCustomControls');
        this._seekLabelTimeout = null;
        this._seekControlsTimeout = null;
        this._lastSeekDirection = null;
        this._seekCounter = 0;
        this._currentTime = null;
        this._currentTimeInterval = null;
        this._isTouchDevice = 'ontouchstart' in window;
        this._isHoveringProgressControl = false;
        this._isHoveringControlBar = false;

        const nextVideoContext = window.__NEXT_VIDEO_CONTEXT__ || {};
        this._countdown = new VideoCountdown({
            nextVideoAPIUrl: nextVideoContext.nextVideoAPIUrl,
            initialTime: 0,
            videoCountdownCompletedUrl: nextVideoContext.videoCountdownCompletedUrl,
        });

        this.pixelTracker = this._setupTrackingPixel();
        this.isStaticFixedLayout = false;
    }

    async initialize(
        playerElement,
        brightcoveId,
        accountId,
        playerId,
        autoplay = context.autoplay,
        prerollServerUrl = context.prerollServerUrl,
    ) {
        /*
            autoplay scenarios:
            direct page visit - autoplay: 'any'; muted: true;
            domain navigation - autoplay: 'any'; muted: false;
            same page refresh - autoplay: 'any'; muted: true;

            Using 'any' instead of boolean true makes Video.js manage the autoplay
            attempt and fire 'autoplay-success' / 'autoplay-failure' events.
            'any' tries unmuted first, then falls back to muted autoplay before
            reporting failure.
        */
        const navigationEntries = performance.getEntriesByType('navigation');
        const pageReloaded = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
        let muted = pageReloaded || !!autoplay;

        if (!pageReloaded && document.referrer) {
            const referrerUrl = new URL(document.referrer);

            if (referrerUrl.hostname === window.location.hostname) {
                // same domain referrer
                muted = false;
            }
        }

        // R1 app gate is blocking the page — force mute so audio doesn't play under the sheet.
        if (window.__VUMEDI_CONTEXT__?.appGateActive) {
            muted = true;
        }

        // Convert boolean autoplay to Video.js string value for event support.
        // Using 'any' makes Video.js try unmuted first, then fall back to muted
        // autoplay, and fire 'autoplay-success' / 'autoplay-failure' events.
        const autoplayOption = autoplay ? 'any' : false;
        this._autoplayRequested = !!autoplay;
        this._initialMuted = muted;

        this._player =
            playerElement &&
            (await brightcovePlayerLoader({
                refNode: playerElement,
                refNodeInsert: brightcovePlayerLoader.REF_NODE_INSERT_REPLACE,
                accountId,
                playerId,
                embedId: 'default',
                videoId: brightcoveId,
                options: {
                    // Videojs options
                    controls: true,
                    preload: 'auto',
                    fluid: true,
                    autoplay: autoplayOption,
                    muted,
                    playsinline: true,
                    controlBar: {
                        remainingTimeDisplay: false,
                    },
                },
            }).then((success) => success.ref));

        this._player.ima3.settings = {
            ...this._player.ima3.settings,
            debug: false,
            serverUrl: prerollServerUrl || '',
            timeout: 5000,
            requestMode: 'onload',
            debugContribAds: false,
            disableCustomPlaybackForIOS10Plus: true,
            showVpaidControls: true,
            vpaidMode: 'DISABLED',
        };

        createCustomComponents(this);

        this._player.on('loadedmetadata', () => {
            // if the `t` query parameter is added, start the video from the value set for the param
            const videoDuration = this._player.duration();
            const queryTimestamp = context.timestamp;

            if (queryTimestamp && queryTimestamp < videoDuration) {
                this._player.currentTime(queryTimestamp);
            }
        });

        this._player.on('loadeddata', () => {
            // move the current timestamp inside the thumbnail preview element
            const timestampElement = document.querySelector('.vjs-time-tooltip');
            const thumbnailElement = document.querySelector('.vjs-thumbnail-display');
            thumbnailElement?.append(timestampElement);
        });

        this._player.on('play', () => {
            const muteButton = document.querySelector('.vjs-mute-control');

            if (this._player.muted()) {
                muteButton.classList.add('vjs-vol-0');
            } else {
                muteButton.classList.remove('vjs-vol-0');
            }
        });

        if (this._player) {
            this._onPlayerReadyHandlers(this._player);
            this._player.controlBar.playbackRateMenuButton.handleClick = () => {}; // disable default behavior
        }

        this._videoControlsHandler();
        this._videoChaptersControls(this._player);
        this._videoLayoutHandler();

        this._currentTimeInterval = setInterval(() => {
            if (!this._player) {
                clearInterval(this._currentTimeInterval);
                return;
            }
            this._currentTime = this._player.currentTime();
        }, 1000);

        // Expose player
        window.player = this._player;
        return this._player;
    }

    /**
     * Set video player to fixed layout, i.e. user can scroll below the player while the player is "glued" to the upper
     * part of the screen. Same as YouTube on mobile devices.
     *
     * Static mode prevents user from exiting the fixed layout by scrolling up. Used when the video AI assistant is
     * opened on mobile devices. To disable static fixed layout, method `unsetFixedLayout(true)` must be called.
     *
     * @param {boolean} isStatic Whether to enable static fixed layout. Must be disabled with `unsetFixedLayout(true)`.
     */
    setFixedLayout(isStatic = false) {
        if (this.isStaticFixedLayout) {
            return;
        }

        if (isStatic) {
            this.isStaticFixedLayout = true;
        }

        const playerHeight = this._player.el_.getBoundingClientRect().height;

        this._player.el_.classList.add('vjs-fixed-position');
        this._player.el_.parentElement.style.setProperty('height', `${playerHeight}px`);
    }

    unsetFixedLayout(removeStatic = false) {
        if (this.isStaticFixedLayout && !removeStatic) {
            return;
        }

        this.isStaticFixedLayout = false;
        this._player.el_.classList.remove('vjs-fixed-position');
        this._player.el_.parentElement.style.removeProperty('height');
    }

    getPlayerElement() {
        return this._player?.el_;
    }

    /**
     * Sourced from https://stackoverflow.com/a/9039885/2711654
     * Note that `navigator.platform` is deprecated. Its replacement is `navigator.userAgentData`
     * but that's not yet properly supported. That's why the deprecated variant is still used.
     *
     * @returns {boolean|boolean} True if estimated that the client is running on iOS
     */
    _isRunningOnIOS() {
        return (
            ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
                navigator.platform,
            ) ||
            // iPad on iOS 13 detection
            (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
        );
    }

    _onPlayerReadyHandlers(player) {
        this._handlePlayPermission(player);

        if (context?.updateViewedTimeUrl && context.viewedUUID) {
            this._handlePrerollEventsTracking(player);
            this._handlePlayerTimeTracking(player);
            this._handlePlayerFullscreenTracking(player);
            this._handleOrientationTracking();
        }

        // User settings
        player.defaultPlaybackRate(this._loadUserPlaybackRate());
        player.volume(this._loadUserVolumeLevel());
    }

    _setupTrackingPixel() {
        const pixelContext = window.__TRACKING_PIXEL_CONTEXT__;
        if (!pixelContext) return null;

        const container = document.querySelector('main.container');
        if (!container) return null;

        return new PixelTracker({ pixelContext, container }, context.videoId);
    }

    _setReportingTime(playerProgressSeconds, now, chapterId = undefined) {
        this._lastReportTime = now;
        this._lastPlayerProgressSeconds = Math.max(this._lastPlayerProgressSeconds, playerProgressSeconds);
        if (this.hasChapters && chapterId !== undefined) {
            this._lastPlayerProgressPerChapterSeconds[chapterId] = Math.max(
                this._lastPlayerProgressPerChapterSeconds[chapterId],
                playerProgressSeconds,
            );
        }
    }

    _checkAndReportPlayedTime(playerProgressSeconds, forceUpdate = false, seek = false) {
        const now = new Date().getTime();
        const playerProgressMs = playerProgressSeconds * 1000;

        let chapterId;
        let lastChapterProgressSeconds = Number.MAX_SAFE_INTEGER; // if no chapters then min will pick video progress
        if (this.hasChapters) {
            chapterId = this._findActiveChapterForCurrentTime(playerProgressMs).dataset.chapterId;
            lastChapterProgressSeconds = this._lastPlayerProgressPerChapterSeconds[chapterId];
        }
        const lastPlayerProgressSeconds = Math.min(lastChapterProgressSeconds, this._lastPlayerProgressSeconds);

        if (playerProgressSeconds < lastPlayerProgressSeconds) return;
        if (
            now - this._lastReportTime > this._reportTimeInterval / this._player.playbackRate() ||
            (seek && playerProgressSeconds - lastPlayerProgressSeconds > 10) || // Prevent reporting short seeks
            forceUpdate
        ) {
            this._setReportingTime(playerProgressSeconds, now, chapterId);

            const postJson = JSON.stringify({ viewed_time: playerProgressMs });
            const headers = {
                'X-CSRFToken': Cookies.get('csrftoken') || context.csrftoken,
            };

            // keepalive lets the request finish even if the user navigates away mid-flight,
            // so cancelled-on-unload errors stop reaching Sentry as actionable noise.
            const controller = new AbortController();
            let didTimeout = false;
            const timeoutId = setTimeout(() => {
                didTimeout = true;
                controller.abort();
            }, 10000);

            fetch(context?.updateViewedTimeUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    ...headers,
                },
                body: postJson,
                keepalive: true,
                credentials: 'same-origin',
                signal: controller.signal,
            })
                .then(async (response) => {
                    if (!response.ok) {
                        const rawResponse = await response.text();
                        let errorDetail;
                        try {
                            const json = JSON.parse(rawResponse);
                            errorDetail = json.detail || JSON.stringify(json);
                        } catch {
                            errorDetail = rawResponse || `Status: ${response.status}`;
                        }
                        throw new Error(`HTTP ${response.status}: ${errorDetail}`);
                    }

                    // eslint-disable-next-line no-console
                    console.log(`Reporting viewed time ${playerProgressMs}`);
                })
                .catch((err) => {
                    if (err.name === 'AbortError') {
                        if (!didTimeout) {
                            // Browser-initiated abort (page unload, keepalive quota, etc.).
                            // Not actionable — drop it.
                            return;
                        }
                        Sentry.withScope((scope) => {
                            scope.setFingerprint(['[TIMEOUT] Set viewed time']);
                            Sentry.captureException(
                                new Error(
                                    `The request has timed out. Data: ${postJson}, Headers: ${JSON.stringify(
                                        headers,
                                    )}, viewed UUID: ${context.viewedUUID}.`,
                                ),
                            );
                        });
                    } else {
                        Sentry.withScope((scope) => {
                            scope.setFingerprint(['Set viewed time error']);
                            Sentry.captureException(
                                new Error(
                                    `Something went wrong with reporting viewed_time. Data: ${postJson}, Headers: ${JSON.stringify(
                                        headers,
                                    )}, viewed UUID: ${context.viewedUUID}, Err: ${err.message || 'Unknown error'}.`,
                                ),
                            );
                        });
                    }
                })
                .finally(() => clearTimeout(timeoutId));
        }
    }

    _getCurrentAdData() {
        const { ima3 } = this._player;

        try {
            for (const value of Object.values(ima3.currentAd)) {
                if ('adWrapperIds' in value) {
                    return value;
                }
            }
            throw new Error('Missing adWrapperIds in values.');
        } catch (err) {
            Sentry.withScope((scope) => {
                scope.setFingerprint(['Preroll ad data error']);
                Sentry.captureException(
                    new Error(
                        `Can not get current ad data from ima3. ima3: ${JSON.stringify(ima3)}, Err: ${JSON.stringify(
                            err,
                        )}.`,
                    ),
                );
            });
        }

        return null;
    }

    _setGoogleAdData() {
        if (!this.lineItemId) {
            const currentAd = this._getCurrentAdData();
            if (currentAd.adWrapperCreativeIds.length) {
                [this.lineItemId] = currentAd.adWrapperIds;
                [this.creativeId] = currentAd.adWrapperCreativeIds;
            } else {
                this.lineItemTitle = currentAd.title;
                this.lineItemId = currentAd.adId;
                this.creativeId = currentAd.creativeId;
            }

            if (Number.isNaN(this.lineItemId % 1) || Number.isNaN(this.creativeId % 1)) {
                Sentry.withScope((scope) => {
                    scope.setFingerprint(['Preroll ad data error']);
                    scope.addAttachment({ filename: 'currentAd.txt', data: JSON.stringify(currentAd) });
                    Sentry.captureException(
                        new Error(`Invalid line item ${this.lineItemId} or creative ${this.creativeId}.`),
                    );
                    scope.clearAttachments();
                });
            }
        }
    }

    _report_perroll_viewed_time(skipped = false, clicked = false, finished = false) {
        const { ad } = this._player.ads;
        let viewedTime = ad.currentTime || 1;

        if (finished) {
            viewedTime = ad.duration;
        }

        this._setGoogleAdData();

        const postData = JSON.stringify({
            viewed_uuid: context.viewedUUID,
            line_item_title: this.lineItemTitle,
            line_item_id: this.lineItemId,
            creative_id: this.creativeId,
            viewed_time: `${viewedTime * 1000}`,
            skipped,
            clicked,
            finished,
        });
        const headers = {
            'X-CSRFToken': Cookies.get('csrftoken') || context.csrftoken,
        };
        $.ajax({
            url: context.prerollAnalyticsUrl,
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            data: postData,
            type: 'POST',
            headers,
        })
            .then(() => {
                // eslint-disable-next-line no-console
                console.log(`Reporting preroll viewed time ${viewedTime * 1000}`, skipped, clicked, finished);
            })
            .catch((err) => {
                Sentry.withScope((scope) => {
                    scope.setFingerprint(['Set preroll viewed time error']);
                    Sentry.captureException(
                        new Error(
                            `Something went wrong with reporting preroll viewed_time. Data: ${postData}, Err: ${JSON.stringify(
                                err,
                            )}.`,
                        ),
                    );
                });
            });
    }

    _showSafetyInformation() {
        this._setGoogleAdData();
        $.ajax({
            url: context.videoSafetyInformationUrl,
            data: {
                line_item_id: this.lineItemId,
                creative_id: this.creativeId,
            },
        }).success((data) => {
            $('#video-safety-information-box').html(data);
        });
    }

    _hideSafetyInformation() {
        $('#video-safety-information-box').html('');
    }

    _handlePlayPermission(player) {
        if (!context.playPermission) {
            player.el_.classList.add('vjs-login-required');
            player.on('play', () => {
                player.pause();
                $('#loginOrRegisterModal').modal('show');
            });
        }
    }

    /**
     * Using this.lineItemTitle, this.lineItemId, and this.creativeId properties.
     */
    _handlePrerollEventsTracking(player) {
        // [PY-2386] Viewed time tracking patch for autoplay videos
        player.on('adserror', (e) => {
            if (context.autoplay) {
                e.target.player.trigger('contentresumed');
            }
        });
        player.on('ads-ad-started', () => {
            this._showSafetyInformation();
            this._report_perroll_viewed_time();
        });
        player.on('ads-ad-ended', () => {
            this._report_perroll_viewed_time(false, false, true);
            this._hideSafetyInformation();
        });
        player.on('ads-ad-skipped', () => {
            this._report_perroll_viewed_time(true);
            this._hideSafetyInformation();
        });
        player.on('ads-click', () => {
            this._report_perroll_viewed_time(false, true);
        });

        // Report banner viewed time
        player.on('ads-first-quartile', () => {
            this._report_perroll_viewed_time();
        });
        player.on('ads-midpoint', () => {
            this._report_perroll_viewed_time();
        });
        player.on('ads-third-quartile', () => {
            this._report_perroll_viewed_time();
        });
        window.addEventListener('beforeunload', () => {
            if (this.ads) {
                this._report_perroll_viewed_time();
            }
        });
    }

    _handlePlayerTimeTracking(player) {
        this._checkAndReportPlayedTime(0, true); // Sets viewed time to 0

        const onPlaybackStarted = (trigger) => {
            const reportTime = player.currentTime();
            this._checkAndReportPlayedTime(reportTime || 1, true); // Sometimes reportTime can be 0.
            this.pixelTracker?.trackInitialPlay();

            window.vumedi.mixpanel?.track('VideoPlaybackStarted', {
                video_id: context?.videoId || null,
                video_slug: context?.videoSlug || null,
                channel_id: context?.channelId || null,
                trigger,
                muted: player.muted(),
                template_version: context.templateVersion,
                surface: this._surface,
            });
        };

        if (this._autoplayRequested) {
            // When autoplay is requested, Video.js fires a 'play' event even if the
            // browser blocks it (a ghost event). We avoid player.one('play') here and
            // instead use the autoplay-success/failure events to know what really happened.
            player.one('autoplay-success', () => {
                onPlaybackStarted('autoplay');
            });
            player.one('autoplay-failure', () => {
                window.vumedi.mixpanel?.track('VideoAutoplayBlocked', {
                    viewed_uuid: context.viewedUUID,
                    video_id: context?.videoId || null,
                    video_slug: context?.videoSlug || null,
                    channel_id: context?.channelId || null,
                    muted: this._initialMuted,
                    template_version: context.templateVersion,
                    surface: this._surface,
                });
                // Autoplay was blocked. Wait for the user to actually click play.
                player.one('play', () => {
                    onPlaybackStarted('user');
                });
            });
        } else {
            player.one('play', () => {
                onPlaybackStarted('user');
            });
        }
        player.on('ended', (e) => {
            this._checkAndReportPlayedTime(e.target.player.duration(), true);

            window.vumedi.mixpanel?.track('VideoEnded', {
                viewed_uuid: context.viewedUUID,
                video_id: context.videoId,
                video_slug: context.videoSlug || null,
                channel_id: context.channelId || null,
                template_version: context.templateVersion,
                surface: this._surface,
            });

            window.redirectEnabled = true;
            const productWorkshopContext = window.__PRODUCT_WORKSHOP_CONTEXT__;
            // If productWorkshopContext is set countdown will be handled in product_workshop.js file.
            if (productWorkshopContext) {
                if (productWorkshopContext.activityPreTest && !productWorkshopContext.preTestSubmission) {
                    $('#pre-test-modal').modal('show');
                } else if (productWorkshopContext.activityPostTest && !productWorkshopContext.postTestSubmission) {
                    $('#post-test-modal').modal('show');
                } else {
                    // If all tests solved only then run countdown at the end of a video.
                    this._countdown.run();
                }
            } else {
                this._countdown.run();
            }
        });
        player.on('playing', () => {
            this._countdown.hide();
        });
        player.on('seeked', (e) => {
            const eventPlayer = e.target.player;
            const reportTime = eventPlayer.currentTime();

            this._checkAndReportPlayedTime(reportTime, false, true);
            this.pixelTracker?.update(reportTime, eventPlayer.duration());

            // sanity check for seek parameter
            if (!reportTime || reportTime <= this._lastPlayerProgressSeconds) return;
            // const seekedAmount = (reportTime - this._lastPlayerProgressSeconds); TODO
            gtag('event', 'seek_forward_time', { event_category: 'Video', event_label: context?.videoId });
        });
        player.on('timeupdate', (e) => {
            // If manuallyTriggered is true that means someone clicked on the scrubber (this is reported in seeked event).
            const manuallyTriggered = !!e.manuallyTriggered;
            if (!manuallyTriggered) {
                const eventPlayer = e.target.player;
                this._checkAndReportPlayedTime(eventPlayer.currentTime());
                this.pixelTracker?.update(eventPlayer.currentTime(), eventPlayer.duration());
            }
        });

        window.addEventListener('beforeunload', () => {
            const reportTime = player.currentTime();

            if (reportTime) {
                this._checkAndReportPlayedTime(reportTime, true);
            }
        });
    }

    _handlePlayerFullscreenTracking(player) {
        player.on('fullscreenchange', () => {
            if (player.isFullscreen()) {
                window.eventTracker.trackFullscreenOn();
            } else {
                window.eventTracker.trackFullscreenOff();
            }
        });
    }

    _handleOrientationTracking() {
        // Track only playable videos
        if (!context.playPermission) {
            return;
        }
        const portrait = window.matchMedia('(orientation: portrait)');
        // Track initial orientation
        if (portrait.matches) {
            window.eventTracker.trackOrientationPortrait();
        } else {
            window.eventTracker.trackOrientationLandscape();
        }
        // Track consecutive orientations
        portrait.addEventListener('change', (e) => {
            if (e.matches) {
                window.eventTracker.trackOrientationPortrait();
            } else {
                window.eventTracker.trackOrientationLandscape();
            }
        });
    }

    _loadUserPlaybackRate() {
        return UserStore.loadPlaybackRate() || 1;
    }

    _saveUserPlaybackRate(playbackRate) {
        UserStore.savePlaybackRate(playbackRate);
    }

    _loadUserVolumeLevel() {
        const savedVolumeLevel = UserStore.loadVolumeLevel();

        return savedVolumeLevel === undefined ? 1 : savedVolumeLevel;
    }

    _saveUserVolumeLevel(level) {
        UserStore.saveVolumeLevel(level);
    }

    _handleVolumeChange() {
        const volume = this._player.muted() ? 0 : this._player.volume();

        if (volume === 0) {
            this._player.muted(true);
        }

        this._saveUserVolumeLevel(volume);
    }

    _findActiveChapterForCurrentTime(playerProgressMs) {
        return this.chapterItems.find((item) => {
            const { timestampStart, timestampEnd } = item.dataset;
            return playerProgressMs >= timestampStart && playerProgressMs < timestampEnd;
        });
    }

    _handlePlayerRateChange() {
        const currentPlaybackRate = this._player.playbackRate();
        this._saveUserPlaybackRate(currentPlaybackRate);
        this._checkAndReportPlayedTime(this._player.currentTime(), true); // report and reset tracking
        this._trackPlaybackRateChange(currentPlaybackRate);
    }

    _videoChaptersControls(player) {
        // TODO: some of the variables below should probably be class properties.
        this.chaptersContainer = document.querySelector('.js_chaptersContainer');
        this.hasChapters = !!this.chaptersContainer;

        if (!this.hasChapters) return;

        const videoElement = document.querySelector('video-js');
        this.chapterItems = Array.from(this.chaptersContainer.querySelectorAll('.js_chapterItem'));
        this._trackChapterImpression();

        // player progress in here is not relative to chapter
        this._lastPlayerProgressPerChapterSeconds = {};
        for (const chapterItem of this.chapterItems) {
            const { chapterId, timestampStart } = chapterItem.dataset;
            this._lastPlayerProgressPerChapterSeconds[chapterId] = timestampStart / 1000;
        }

        /**
         * Click listener that is called whenever a chapter is changed, caused by following:
         *
         * - Initial playout of the video, i.e. initialization of chapters
         * - Video progress naturally enters into new chapter.
         * - User clicks inside a progress bar somewhere inside non-active chapter.
         * - User clicks on a chapter inside chapter list.
         */
        let chapterViewEndedForVideoEnd = false;
        let isSeeking = false;

        // Wall-clock play time tracking for ChapterViewed duration
        let chapterPlayStartedAt = null;
        let chapterAccumulatedPlayTimeMs = 0;
        let isPlayingInChapter = false;

        const flushPlayTime = () => {
            if (isPlayingInChapter && chapterPlayStartedAt !== null) {
                chapterAccumulatedPlayTimeMs += Date.now() - chapterPlayStartedAt;
                chapterPlayStartedAt = null;
            }
            isPlayingInChapter = false;
        };

        const resetPlayTimeForNewChapter = () => {
            chapterAccumulatedPlayTimeMs = 0;
            chapterPlayStartedAt = null;
            isPlayingInChapter = false;
            if (!player.paused() && !player.ended()) {
                chapterPlayStartedAt = Date.now();
                isPlayingInChapter = true;
            }
        };

        const getCurrentPlayTimeMs = () => {
            let total = chapterAccumulatedPlayTimeMs;
            if (isPlayingInChapter && chapterPlayStartedAt !== null) {
                total += Date.now() - chapterPlayStartedAt;
            }
            return total;
        };

        const emitChapterViewed = (activeItem, transitionTrigger, nextItem = null) => {
            if (!activeItem) return;

            const {
                timestampStart: chapterStart,
                timestampEnd: chapterEnd,
                chapterId: chapterId,
                chapterOrdinal: chapterOrdinal,
                chapterTitle: chapterTitle,
            } = activeItem.dataset;

            if (!chapterStart || !chapterEnd) return;

            const chapterStartMs = parseInt(chapterStart, 10);
            const chapterEndMs = parseInt(chapterEnd, 10);
            const chapterDurationMs = Math.max(chapterEndMs - chapterStartMs, 0);

            flushPlayTime();
            const viewDurationMs = getCurrentPlayTimeMs();

            const newChapterPosition = nextItem?.dataset?.chapterOrdinal
                ? parseInt(nextItem.dataset.chapterOrdinal, 10)
                : null;

            window.vumedi.mixpanel?.track('ChapterViewed', {
                viewed_uuid: context.viewedUUID,
                video_id: context.videoId,
                video_slug: context.videoSlug,
                chapter_id: chapterId,
                chapter_title: chapterTitle,
                current_chapter_position: parseInt(chapterOrdinal, 10),
                new_chapter_position: newChapterPosition,
                total_chapters: this.chapterItems.length,
                chapter_duration_ms: chapterDurationMs,
                view_duration_ms: viewDurationMs,
                transition_trigger: transitionTrigger,
                template_version: context.templateVersion,
            });
        };

        const chapterClickHandler = (item, shouldSkipToChapter = true, transitionTrigger = 'chapter_click') => {
            const { timestampStart, chapterId, chapterOrdinal, chapterTitle } = item.dataset;
            const previousActiveItem = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            const previousActiveItemId = previousActiveItem?.dataset.chapterId;
            const newActiveItemId = item?.dataset.chapterId;

            if (previousActiveItem && newActiveItemId !== previousActiveItemId) {
                emitChapterViewed(previousActiveItem, transitionTrigger, item);
                resetPlayTimeForNewChapter();
            }

            window.dispatchEvent(new CustomEvent(EVENT_PLAYER_CHAPTER_CHANGE, { detail: { chapterId } }));
            document.querySelector('.vjs-custom-metadata-chapter-title span').innerText = chapterTitle;

            // scroll the user back to the video
            if (window.innerWidth < 768 && shouldSkipToChapter) {
                setTimeout(() => {
                    videoElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                }, 100);
            }

            previousActiveItem?.classList.remove('is-active');
            item.classList.add('is-active');

            this.chaptersContainer.setAttribute('active-chapter-index', chapterOrdinal - 1);

            if (shouldSkipToChapter) {
                const videoTimestampMiliseconds = Math.floor(this._currentTime * 1000);

                // Make the timeline appear and stay active while the chapter is being changed.
                // The `currentTime` method makes the user inactive, which causes the timeline to disappear.
                // By listening to `userinactive` event and making the user active again,
                // we keep the timeline visible during chapter change.
                const keepActive = () => player.userActive(true);
                player.on('userinactive', keepActive);

                player.userActive(true);

                player.one('seeked', () => {
                    player.off('userinactive', keepActive);
                });

                player.currentTime(timestampStart / 1000);

                // clicking on a chapter will automatically continue video playback if the video is paused
                item.classList.remove('is-paused');

                if (player.paused()) {
                    player.play();
                }

                // Send events only if the clicked chapter is not the already active one
                if (newActiveItemId !== previousActiveItemId) {
                    window.eventTracker.trackChapterClick(
                        videoTimestampMiliseconds,
                        chapterOrdinal,
                        chapterTitle,
                        timestampStart,
                    );
                }
            }
        };

        /**
         * Listener that automatically changes the active chapter based on the video progress.
         *
         * Listener is called when progress naturally enters into new chapter, or when a user clicks
         * inside a progress bar somewhere inside non-active chapter.
         *
         * This listener is not called when a user clicks a chapter inside chapter list.
         */
        const triggerChapterChangeBasedOnCurrentPlayheadTime = (currentTime, transitionTrigger) => {
            const chapterForTimestamp = this._findActiveChapterForCurrentTime(currentTime);
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');

            if (chapterForTimestamp && chapterForTimestamp !== activeChapter) {
                chapterClickHandler(chapterForTimestamp, false, transitionTrigger);
            }
        };

        player.on('pause', () => {
            flushPlayTime();
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            if (activeChapter) {
                // add class to display "Paused" label
                activeChapter.classList.add('is-paused');
            }
        });

        player.on('play', () => {
            if (!isPlayingInChapter) {
                chapterPlayStartedAt = Date.now();
                isPlayingInChapter = true;
            }
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            // remove class to hide "Paused" label
            activeChapter?.classList.remove('is-paused');
        });

        player.on('seeking', () => {
            flushPlayTime();
            isSeeking = true;
        });

        player.on('seeked', () => {
            const currentTime = player.currentTime() * 1000;
            triggerChapterChangeBasedOnCurrentPlayheadTime(currentTime, 'seek');
            isSeeking = false;
            if (!isPlayingInChapter && !player.paused() && !player.ended()) {
                chapterPlayStartedAt = Date.now();
                isPlayingInChapter = true;
            }
        });

        let lastExecutionTime = 0;
        let thumbnailPreviewMoved = false;

        player.on('timeupdate', () => {
            if (isSeeking) return;

            const currentTime = player.currentTime();
            if (lastExecutionTime === 0 || currentTime - lastExecutionTime >= 10 || lastExecutionTime > currentTime) {
                lastExecutionTime = currentTime;
                triggerChapterChangeBasedOnCurrentPlayheadTime(currentTime * 1000, 'auto_advance');
            }

            if (!thumbnailPreviewMoved) {
                thumbnailPreviewMoved = true;
                const thumbnailPreview = document.querySelector('.vjs-thumbnail-display');
                const thumbnailPreviewParent = thumbnailPreview?.parentNode;
                thumbnailPreviewParent?.insertAdjacentElement('afterend', thumbnailPreview);
            }
        });

        player.one('loadedmetadata', () => {
            const highlightProgressElement = document.createElement('div');
            highlightProgressElement.classList.add('video-chapters__progress-bar', 'js_chapterProgressBar');

            const seekBarElement = player.controlBar.progressControl.seekBar.el();
            seekBarElement.append(highlightProgressElement);

            const progressControlElement = player.controlBar.progressControl.el();

            // Keep player controls visible while hovering over progress control area
            progressControlElement.addEventListener('mouseenter', () => {
                this._isHoveringProgressControl = true;
                player.userActive(true);
            });

            progressControlElement.addEventListener('mouseleave', () => {
                this._isHoveringProgressControl = false;
            });

            progressControlElement.addEventListener('mousemove', () => {
                if (this._isHoveringProgressControl) {
                    player.userActive(true);
                }
            });

            let clipPathString = '0 0, ';

            this.chapterItems.forEach((item, index) => {
                const videoDuration = player.duration() * 1000; // Convert to ms

                const { timestampStart, timestampEnd } = item.dataset;
                const markerStartingPosition = `${(timestampStart / videoDuration) * 100}%`;
                const isFirstChapter = index === 0;

                if (parseInt(markerStartingPosition, 10) > 0) {
                    clipPathString += `${markerStartingPosition} 0%, ${markerStartingPosition} 100%, calc(${markerStartingPosition} + 4px) 100%, calc(${markerStartingPosition} + 4px) 0%, `;
                    if (index + 1 === this.chapterItems.length) {
                        clipPathString += '100% 0%, 100% 100%, 0% 100%';
                    }
                }

                seekBarElement.style.setProperty('--progress-holder-polygon-coordinates', clipPathString);

                item.addEventListener('click', () => chapterClickHandler(item, true, 'chapter_click'));

                const highlightClasses = ['visible', `video-chapters__progress-bar--color-${(index % 5) + 1}`];

                item.addEventListener('mouseenter', () => {
                    const startPercentage = (timestampStart / videoDuration) * 100;
                    const endPercentage = (timestampEnd / videoDuration) * 100;

                    Object.assign(highlightProgressElement.style, {
                        left: `calc(${startPercentage}% + ${isFirstChapter ? 0 : 4}px)`,
                        width: `calc(${endPercentage - startPercentage}% - ${isFirstChapter ? 0 : 4}px)`,
                    });

                    highlightProgressElement.classList.add(...highlightClasses);
                });

                item.addEventListener('mouseleave', () => {
                    highlightProgressElement.classList.remove(...highlightClasses);
                });

                // Create invisible hover zone on progress bar for reverse highlight
                const progressHoverZone = document.createElement('div');
                progressHoverZone.classList.add('video-chapters__progress-hover-zone');
                const startPercentage = (timestampStart / videoDuration) * 100;
                const endPercentage = (timestampEnd / videoDuration) * 100;

                Object.assign(progressHoverZone.style, {
                    left: `${startPercentage}%`,
                    width: `${endPercentage - startPercentage}%`,
                });

                progressHoverZone.addEventListener('mouseenter', () => {
                    item.classList.add('is-hovered');

                    // Scroll to hovered chapter
                    this.chaptersContainer.setAttribute('active-chapter-index', index);

                    Object.assign(highlightProgressElement.style, {
                        left: `calc(${startPercentage}% + ${isFirstChapter ? 0 : 4}px)`,
                        width: `calc(${endPercentage - startPercentage}% - ${isFirstChapter ? 0 : 4}px)`,
                    });

                    highlightProgressElement.classList.add(...highlightClasses);
                });

                progressHoverZone.addEventListener('mouseleave', () => {
                    item.classList.remove('is-hovered');

                    // Restore scroll to active chapter
                    const activeItem = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
                    if (activeItem) {
                        const activeIndex = Array.from(this.chapterItems).indexOf(activeItem);
                        this.chaptersContainer.setAttribute('active-chapter-index', activeIndex);
                    }

                    highlightProgressElement.classList.remove(...highlightClasses);
                });

                progressControlElement.append(progressHoverZone);
            });

            // Set initial chapter
            chapterClickHandler(this.chapterItems[0], false, 'init');
            resetPlayTimeForNewChapter();
        });

        player.on('ended', () => {
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            if (activeChapter) {
                emitChapterViewed(activeChapter, 'video_end');
                chapterViewEndedForVideoEnd = true;
            }
        });

        window.addEventListener('beforeunload', () => {
            if (chapterViewEndedForVideoEnd) return;

            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            if (activeChapter) {
                emitChapterViewed(activeChapter, 'page_unload');
            }
        });
    }

    _trackChapterImpression() {
        const chaptersContainer = this.chaptersContainer;
        if (!chaptersContainer) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        window.vumedi.mixpanel?.track('ChapterImpression', {
                            viewed_uuid: context.viewedUUID,
                            video_id: context.videoId,
                            video_slug: context.videoSlug,
                            channel_id: context?.channelId || null,
                            total_chapters: this.chapterItems.length,
                            template_version: context.templateVersion,
                        });
                        observer.unobserve(chaptersContainer);
                    }
                });
            },
            { threshold: 0.5 },
        );

        observer.observe(chaptersContainer);
    }

    _videoControlsHandler() {
        const fullscreenToggleButton = this._player.getDescendant(['ControlBar', 'FullscreenToggle']);
        const loginRequired = this._player?.el_.classList.contains('vjs-login-required');
        const captionsButton = this._player.controlBar.getChild('SubsCapsButton')?.el_;
        const { tracks_: captionTracks } = this._player.textTracks();
        const activeTrack = captionTracks.find(
            (track) => ['captions', 'subtitles'].includes(track.kind) && track.mode === 'showing',
        );
        let captionsShown = false;

        if (activeTrack) {
            // not sure is it possible or not, but
            // in case there is an active track by default
            // add the active class to the captionsButton
            captionsButton.classList.toggle('is-active');
        }

        const toggleCaptions = () => {
            captionsButton.classList.toggle('is-active');
            captionsShown = !captionsShown;
            captionTracks.forEach((track) => {
                if (['captions', 'subtitles'].includes(track.kind)) {
                    track.mode = captionsShown ? 'showing' : 'disabled'; // Use 'hidden' to hide without disabling
                    window.vumedi.mixpanel?.track('CaptionsToggled', {
                        viewed_uuid: context.viewedUUID,
                        value: captionsShown ? 'showing' : 'disabled',
                        template_version: context.templateVersion,
                        surface: this._surface,
                    });
                }
            });
        };

        captionsButton.addEventListener('click', toggleCaptions);
        captionsButton.addEventListener('touchend', toggleCaptions);

        // blur out the fullscreen button to enable keyboard seeking
        fullscreenToggleButton?.on('click', () => {
            fullscreenToggleButton.blur();
        });

        document.addEventListener('keydown', (event) => {
            if (loginRequired) return;

            if (event.key === 'ArrowRight') {
                this._seekVideo('forward', 'key');
            } else if (event.key === 'ArrowLeft') {
                this._seekVideo('backward', 'key');
            }
        });

        this._player.controlBar?.progressControl?.on('mousedown', () => {
            this._seekbarHandler();
        });

        this._player.controlBar?.progressControl?.seekBar?.on('mousedown', () => {
            this._seekbarHandler();
        });

        // Keep controls visible while hovering over control bar
        const controlBarElement = this._player.controlBar?.el();
        if (controlBarElement) {
            controlBarElement.addEventListener('mouseenter', () => {
                this._isHoveringControlBar = true;
                this._player.userActive(true);
            });

            controlBarElement.addEventListener('mouseleave', () => {
                this._isHoveringControlBar = false;
            });

            // Also keep active when clicking on control bar
            controlBarElement.addEventListener('click', () => {
                this._player.userActive(true);
            });
        }

        // Prevent controls from hiding while user is hovering over control bar
        this._player.on('userinactive', () => {
            if (this._isHoveringControlBar || this._isHoveringProgressControl) {
                this._player.userActive(true);
            }
        });

        this._player.on('ratechange', () => {
            this._handlePlayerRateChange();
        });
    }

    _videoLayoutHandler() {
        let scrollOffset = null;

        // Extended logic for `scrollOffset` is needed to support orientation switch from 'mobile' to 'desktop' views
        window.addEventListener('scroll', () => {
            if (window.innerWidth < 992) {
                if (!scrollOffset) {
                    scrollOffset = Math.floor(this._player.el_.getBoundingClientRect().top + window.scrollY);
                }

                if (window.scrollY > scrollOffset) {
                    this.setFixedLayout();
                } else {
                    this.unsetFixedLayout();
                }
            } else {
                scrollOffset = null;
            }
        });
    }

    _trackPlaybackRateChange(playbackRate) {
        const eventTimestampMiliseconds = Math.floor(this._currentTime * 1000);
        window.eventTracker.trackPlaybackRateChange(playbackRate, eventTimestampMiliseconds);
        gtag('event', 'playback_rate_changed', {
            event_category: 'Video',
            event_label: context?.videoId,
            playback_rate: playbackRate,
            video_timestamp: eventTimestampMiliseconds,
        });
        window.vumedi.mixpanel?.track('PlaybackRateChanged', {
            viewed_uuid: context.viewedUUID,
            playback_rate: playbackRate,
            video_timestamp: eventTimestampMiliseconds,
            template_version: context.templateVersion,
            surface: this._surface,
        });
    }

    _seekbarHandler() {
        const seekedTime = this._player.currentTime();
        const direction = seekedTime > this._currentTime ? 'forward' : 'backward';
        this._seekVideo(direction, 'scrubber');
    }

    _startControlsTimeout(eventName, customControlsWrap) {
        // the seek controls will dissapear after 3 seconds unless another action (seek forward/backward) is triggered
        clearTimeout(this._seekControlsTimeout);
        this._seekControlsTimeout = setTimeout(() => {
            // Don't hide controls if user is hovering over control bar or progress control
            if (this._isHoveringControlBar || this._isHoveringProgressControl) {
                return;
            }
            if (['mouseover', 'touchend', 'click'].includes(eventName)) {
                customControlsWrap?.classList.add('disable-hover-styles');
            }
            customControlsWrap?.classList.remove('show-seek-controls');
            setTimeout(() => {
                this._player.userActive(false);
            }, 1);
        }, 3000);
    }

    _seekVideo(direction, source) {
        const eventTimestampMiliseconds = Math.floor(this._currentTime * 1000);
        const currentTime = this._player.currentTime();
        const duration = Math.floor(this._player.duration());
        const remainingTime = duration - this._currentTime;
        const directionTitleCase = direction.charAt(0).toUpperCase() + direction.slice(1);
        const sourceTitleCase = source.charAt(0).toUpperCase() + source.slice(1);

        window.vumedi.mixpanel?.track('VideoSeekTriggered', {
            viewed_uuid: context.viewedUUID,
            video_timestamp: eventTimestampMiliseconds,
            direction: directionTitleCase,
            source: sourceTitleCase,
            template_version: context.templateVersion,
            surface: this._surface,
        });

        gtag('event', 'video_seek_triggered', {
            event_category: 'Video',
            event_label: context?.videoId,
            video_timestamp: eventTimestampMiliseconds,
            direction: directionTitleCase,
            source: sourceTitleCase,
        });

        window.eventTracker.trackVideoSeek(sourceTitleCase, directionTitleCase, eventTimestampMiliseconds);

        if (!['button', 'key'].includes(source)) return;

        this._startControlsTimeout();

        if (direction === 'forward') {
            if (remainingTime >= 10) {
                this._player.currentTime(currentTime + 10);
                this._showSeekLabel('forward');
            } else {
                this._player.currentTime(duration);
            }
        } else if (currentTime >= 10) {
            this._player.currentTime(currentTime - 10);
            this._showSeekLabel('backward');
        } else {
            this._player.currentTime(0);
        }
    }

    _showSeekLabel(direction) {
        const seekLabel = document.querySelector('.js_videoSeekLabel');
        clearTimeout(this._seekLabelTimeout);

        if (direction !== this._lastSeekDirection) {
            this._seekCounter = 0;
        }

        this._lastSeekDirection = direction;
        seekLabel?.classList.remove('playing-forward', 'playing-backward');
        this._seekCounter += direction === 'forward' ? 1 : -1;
        seekLabel?.classList.add(`playing-${direction}`, 'is-visible');

        const labelParagraph = seekLabel?.querySelector('p');
        if (labelParagraph) {
            labelParagraph.innerText = `${Math.abs(this._seekCounter * 10)} seconds`;
        }

        this._seekLabelTimeout = setTimeout(() => {
            seekLabel?.classList.remove('is-visible');
            this._seekCounter = 0;
            this._lastSeekDirection = null;
        }, 2000);
    }
}

export class EmbeddedBrightcovePlayer extends BrightcovePlayer {
    _onPlayerReadyHandlers() {}
}

/**
 * Brightcove player with view-time tracking but without page-specific
 * features (countdown, orientation, fullscreen, preroll tracking).
 * Used in the Askmedi detail panel where the full video page DOM is absent.
 */
export class TrackingBrightcovePlayer extends BrightcovePlayer {
    _onPlayerReadyHandlers(player) {
        if (context?.updateViewedTimeUrl && context.viewedUUID) {
            this._handlePlayerTimeTracking(player);
        }

        player.defaultPlaybackRate(this._loadUserPlaybackRate());
        player.volume(this._loadUserVolumeLevel());
    }
}

export class AskmediBrightcovePlayer extends TrackingBrightcovePlayer {
    constructor() {
        super(...arguments);
        this._surface = 'askmedi';
    }
}
